import { useState, useRef, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TreePine } from 'lucide-react';
import treeBg from '@/assets/living-tree-v5-bg.png';
import { FireflyParticles } from './FireflyParticles';
import { FruitNode } from './FruitNode';
import { FruitModal } from './FruitModal';
import { ShareDialog } from '@/components/sharing/ShareDialog';
import { RelationshipPanel } from './RelationshipPanel';
import { EmptyTreeState } from './EmptyTreeState';
import { BranchNudge } from './BranchNudge';
import {
  announcementToFruit,
  groupByRecipient,
  BRANCH_ANCHORS,
  getBranchHealth,
  type TreeFruit,
} from './types';
import type { Announcement } from '@/types/announcement';

interface LivingTreeV5Props {
  announcements: Announcement[];
}

/** Distribute fruits to branch anchor positions */
function assignFruitPositions(fruits: TreeFruit[]) {
  return fruits.map((fruit, i) => {
    const anchor = BRANCH_ANCHORS[i % BRANCH_ANCHORS.length];
    const jitterX = (Math.sin(i * 7.3) * 2);
    const jitterY = (Math.cos(i * 5.1) * 2);
    return {
      fruit,
      x: anchor.x + jitterX,
      y: anchor.y + jitterY,
    };
  });
}

/** Visual stage derived from message count */
function getTreeStage(count: number) {
  let brightness = 0.4;
  if (count > 3) brightness = 0.5;
  if (count > 10) brightness = 0.6;
  if (count > 25) brightness = 0.75;
  if (count > 40) brightness = 0.85;
  if (count > 60) brightness = 0.95;
  if (count >= 100) brightness = 1;

  // Vein/root glow — activates at 3, maxes at 27
  const veinLevel = Math.min(9, Math.floor(count / 3));
  const veinOpacity = veinLevel === 0 ? 0 : 0.1 + (veinLevel / 9) * 0.9;
  const veinsPulse = veinLevel < 9;

  // Trunk glow intensity (0-1)
  let trunkGlow = veinOpacity * 0.5;
  if (count >= 10) trunkGlow = Math.max(trunkGlow, 0.15);
  if (count >= 35) trunkGlow = Math.max(trunkGlow, 0.35);
  if (count >= 50) trunkGlow = Math.max(trunkGlow, 0.5);
  if (count >= 75) trunkGlow = Math.max(trunkGlow, 0.75);
  if (count >= 100) trunkGlow = 1;

  // Sky phase — sunrise kicks in after 27 messages
  let skyPhase = 0;
  if (count >= 25) skyPhase = 1;
  if (count >= 30) skyPhase = 2;
  if (count >= 40) skyPhase = 3;
  if (count >= 60) skyPhase = 3;
  if (count >= 100) skyPhase = 4;

  // Sunrise brightness after 27
  const sunriseLevel = count <= 27 ? 0 : Math.min(1, (count - 27) / 50);

  // Energy flow (root-to-fruit lines)
  let energyFlow = veinOpacity * 0.3;
  if (count >= 25) energyFlow = Math.max(energyFlow, 0.2);
  if (count >= 50) energyFlow = Math.max(energyFlow, 0.5);
  if (count >= 75) energyFlow = Math.max(energyFlow, 0.8);
  if (count >= 100) energyFlow = 1;

  return { brightness, trunkGlow, skyPhase, energyFlow, veinLevel, veinOpacity, veinsPulse, sunriseLevel };
}

/** Background gradient based on sky phase */
function getSkyGradient(phase: number): string {
  switch (phase) {
    case 0: // deep night
      return 'radial-gradient(ellipse at 50% 90%, hsla(160, 30%, 6%, 0.9) 0%, hsla(200, 25%, 4%, 0.95) 100%)';
    case 1: // twilight
      return 'radial-gradient(ellipse at 50% 85%, hsla(220, 20%, 12%, 0.8) 0%, hsla(240, 15%, 8%, 0.9) 60%, hsla(30, 20%, 10%, 0.3) 100%)';
    case 2: // dawn
      return 'radial-gradient(ellipse at 50% 80%, hsla(25, 40%, 18%, 0.6) 0%, hsla(200, 15%, 12%, 0.7) 50%, hsla(35, 50%, 20%, 0.4) 100%)';
    case 3: // sunrise
      return 'radial-gradient(ellipse at 50% 75%, hsla(30, 60%, 25%, 0.5) 0%, hsla(35, 45%, 22%, 0.5) 40%, hsla(40, 55%, 30%, 0.3) 100%)';
    case 4: // daylight
      return 'radial-gradient(ellipse at 50% 60%, hsla(40, 70%, 35%, 0.4) 0%, hsla(35, 50%, 30%, 0.35) 40%, hsla(45, 60%, 40%, 0.2) 100%)';
    default:
      return 'none';
  }
}

// Organic vein paths — imported from shared module
import { ALL_VEIN_PATHS, BRANCH_VEINS, ENERGY_PATHS, getPathWidth, getPathColor, getPathType } from './veinPaths';

export function LivingTreeV5({ announcements }: LivingTreeV5Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedFruit, setSelectedFruit] = useState<TreeFruit | null>(null);
  const [shareDialogFruit, setShareDialogFruit] = useState<TreeFruit | null>(null);
  const [bloomPulse, setBloomPulse] = useState(false);

  // Detect bloom count changes for subtle pulse
  useEffect(() => {
    const currentCount = parseInt(localStorage.getItem('sin_bloom_count') || '0', 10);
    const lastPulsed = parseInt(localStorage.getItem('sin_bloom_pulsed') || '0', 10);
    if (currentCount > lastPulsed) {
      setBloomPulse(true);
      localStorage.setItem('sin_bloom_pulsed', String(currentCount));
      const timer = setTimeout(() => setBloomPulse(false), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const publishedAnnouncements = useMemo(
    () => announcements.filter((a) => a.status === 'approved'),
    [announcements]
  );

  const fruits = useMemo(
    () => publishedAnnouncements.map(announcementToFruit),
    [publishedAnnouncements]
  );

  const branches = useMemo(() => groupByRecipient(fruits), [fruits]);
  const positioned = useMemo(() => assignFruitPositions(fruits), [fruits]);
  const stage = getTreeStage(fruits.length);
  const isEmpty = fruits.length === 0;

  const handleShare = (fruit: TreeFruit) => {
    setSelectedFruit(null);
    setShareDialogFruit(fruit);
  };

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground flex items-center justify-center gap-2 billboard-flash font-heading !leading-tight">
          <TreePine className="h-6 w-6 text-emerald-500" />
          Your Living Tree
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Each fruit is a message you chose not to keep inside.
        </p>
        <p className="text-xs text-muted-foreground/70 mt-0.5 italic">
          💡 The more you share, the more alive your tree becomes
        </p>
      </div>

      {/* Tree + Panel layout */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Tree container */}
        <div
          ref={containerRef}
          className="relative flex-1 min-w-0 -mx-4 lg:-mx-8"
          style={{
            aspectRatio: '4 / 3',
            maxHeight: '750px',
          }}
        >
          {/* Sky / sunrise background — behind everything */}
          <motion.div
            className="absolute inset-0"
            style={{ background: getSkyGradient(stage.skyPhase) }}
            animate={{ opacity: stage.skyPhase > 0 ? 1 : 0 }}
            transition={{ duration: 3, ease: 'easeInOut' }}
          />

          {/* Horizon glow for dawn+ phases */}
          {stage.skyPhase >= 2 && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(ellipse 80% 30% at 50% 85%, hsla(35, 70%, 50%, ${stage.skyPhase >= 4 ? 0.25 : 0.15}) 0%, transparent 70%)`,
              }}
              animate={{
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/* Edge feathering — all 4 sides fade into page background */}
          <div
            className="absolute inset-0 z-[1] pointer-events-none"
            style={{
              background: `
                linear-gradient(to bottom, hsl(var(--background)) 0%, transparent 12%, transparent 85%, hsl(var(--background)) 100%),
                linear-gradient(to right, hsl(var(--background)) 0%, transparent 8%, transparent 92%, hsl(var(--background)) 100%)
              `,
            }}
          />

          {/* Tree image */}
          <motion.div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${treeBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: stage.brightness,
            }}
            animate={{
              scale: [1, 1.02, 1],
              filter: [
                `brightness(${stage.brightness})`,
                `brightness(${stage.brightness * 1.1})`,
                `brightness(${stage.brightness})`,
              ],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Bloom pulse — subtle glow when a reply is received */}
          {bloomPulse && (
            <motion.div
              className="absolute inset-0 pointer-events-none z-[5]"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.35, 0.15, 0.3, 0] }}
              transition={{ duration: 3, ease: 'easeInOut' }}
              style={{
                background: 'radial-gradient(ellipse 60% 50% at 50% 55%, hsla(45, 80%, 60%, 0.25), transparent 70%)',
              }}
            />
          )}

          {/* Trunk glow overlay */}
          {stage.trunkGlow > 0 && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                left: '46%',
                right: '46%',
                top: '25%',
                bottom: '15%',
                background: `linear-gradient(to top, hsla(40, 80%, 50%, ${stage.trunkGlow * 0.4}), hsla(45, 70%, 60%, ${stage.trunkGlow * 0.2}) 50%, transparent 100%)`,
                filter: `blur(${8 + stage.trunkGlow * 12}px)`,
                borderRadius: '50%',
              }}
              animate={{
                opacity: [0.5, 1, 0.5],
                scaleX: [0.9, 1.1, 0.9],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/* ── Vein/root glow system (3+ messages) ── */}
          {stage.veinOpacity > 0 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 110"
              preserveAspectRatio="xMidYMid meet"
              style={{ zIndex: 1 }}
            >
              {ALL_VEIN_PATHS.map((d, i) => {
                const w = getPathWidth(i);
                const type = getPathType(i);
                const colorOpacity = stage.veinOpacity * (type === 'trunk' ? 0.8 : 0.6);
                return (
                  <motion.path
                    key={`vein-${i}`}
                    d={d}
                    stroke={getPathColor(i, colorOpacity)}
                    strokeWidth={w}
                    fill="none"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={stage.veinsPulse ? {
                      pathLength: 1,
                      opacity: [0.3, 0.9, 0.3],
                    } : {
                      pathLength: 1,
                      opacity: 1,
                    }}
                    transition={stage.veinsPulse ? {
                      pathLength: { duration: 2.5, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] },
                      opacity: { duration: 3 + i * 0.15, repeat: Infinity, ease: 'easeInOut', delay: i * 0.1 },
                    } : {
                      pathLength: { duration: 2.5, delay: i * 0.06 },
                    }}
                    style={{
                      filter: `drop-shadow(0 0 ${2 + stage.veinOpacity * 5}px hsla(42, 85%, 55%, ${stage.veinOpacity * 0.5}))`,
                    }}
                  />
                );
              })}
              {/* Energy particles flowing through veins at level 5+ (15 messages) */}
              {stage.veinLevel >= 5 && ENERGY_PATHS.slice(0, 3).map((d, i) => (
                <motion.circle
                  key={`particle-${i}`}
                  r={0.5 + stage.veinOpacity * 0.3}
                  fill={`hsla(45, 90%, 65%, ${stage.veinOpacity * 0.8})`}
                  style={{
                    filter: `blur(0.2px) drop-shadow(0 0 3px hsla(45, 90%, 60%, 0.6))`,
                    offsetPath: `path('${d}')`,
                  }}
                  animate={{
                    offsetDistance: i % 2 === 0 ? ['100%', '0%'] : ['0%', '100%'],
                  }}
                  transition={{
                    duration: 2.5 + i * 0.4,
                    delay: i * 0.8,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
              ))}
            </svg>
          )}

          {/* Sunrise backdrop glow (after 27 messages) */}
          {stage.sunriseLevel > 0 && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `radial-gradient(ellipse 120% 60% at 50% 15%, hsla(40, 85%, 55%, ${stage.sunriseLevel * 0.2}) 0%, hsla(35, 70%, 50%, ${stage.sunriseLevel * 0.08}) 40%, transparent 70%)`,
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/* Fog overlay — less fog as sunrise progresses */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 80%, rgba(11, 27, 22, ${Math.max(0.15, (stage.skyPhase >= 3 ? 0.2 : 0.6) - stage.sunriseLevel * 0.3)}) 0%, transparent 70%)`,
            }}
          />

          {/* Milestone: 50+ messages → soft aura around tree */}
          {fruits.length >= 50 && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at 50% 50%, hsla(40, 80%, 55%, 0.1) 0%, transparent 50%)',
              }}
              animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.98, 1.02, 0.98] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/* Milestone: 100+ messages → radiant crown glow */}
          {fruits.length >= 100 && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                top: '5%',
                left: '25%',
                right: '25%',
                height: '30%',
                background: 'radial-gradient(ellipse at 50% 50%, hsla(45, 90%, 65%, 0.15) 0%, transparent 70%)',
                filter: 'blur(10px)',
              }}
              animate={{ opacity: [0.4, 0.8, 0.4], scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          {/* Milestone: 25+ messages → golden roots visible at base */}
          {fruits.length >= 25 && (
            <svg className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none" width="300" height="80" style={{ opacity: stage.energyFlow > 0 ? 0.6 : 0.4 }}>
              {[
                'M150 0 Q120 30 80 60 Q60 70 30 75',
                'M150 0 Q130 35 110 65 Q100 72 85 78',
                'M150 0 Q170 35 190 65 Q200 72 215 78',
                'M150 0 Q180 30 220 60 Q240 70 270 75',
              ].map((d, i) => (
                <motion.path
                  key={`root-${i}`}
                  d={d}
                  stroke={`hsla(40, 80%, 55%, ${0.4 + stage.energyFlow * 0.3})`}
                  strokeWidth={1.5}
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: i * 0.3, duration: 2, ease: [0.22, 0.61, 0.36, 1] }}
                  style={{ filter: `drop-shadow(0 0 ${4 + stage.energyFlow * 4}px hsla(40, 80%, 55%, 0.3))` }}
                />
              ))}
            </svg>
          )}

          {/* Milestone: 10+ messages → glowing leaves */}
          {fruits.length >= 10 && Array.from({ length: 5 }).map((_, i) => (
            <motion.div
              key={`glow-leaf-${i}`}
              className="absolute pointer-events-none rounded-full"
              style={{
                width: 6,
                height: 6,
                left: `${30 + i * 10}%`,
                top: `${20 + (i * 7) % 30}%`,
                background: `radial-gradient(circle, hsla(${stage.skyPhase >= 3 ? '45, 80%, 60%' : '120, 50%, 50%'}, 0.6), transparent)`,
                filter: 'blur(2px)',
              }}
              animate={{ opacity: [0.2, 0.6, 0.2], scale: [0.8, 1.3, 0.8] }}
              transition={{ duration: 3 + i * 0.5, delay: i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}

          {/* Milestone: 5+ messages → small birds */}
          {fruits.length >= 5 && [
            { x: '25%', y: '18%', delay: 0 },
            { x: '72%', y: '22%', delay: 1.5 },
          ].map((bird, i) => (
            <motion.div
              key={`bird-${i}`}
              className="absolute pointer-events-none"
              style={{ left: bird.x, top: bird.y }}
              animate={{
                y: [0, -3, 0, -2, 0],
                x: [0, 2, -1, 3, 0],
              }}
              transition={{ duration: 6, delay: bird.delay, repeat: Infinity, ease: 'easeInOut' }}
            >
              <svg width="12" height="8" viewBox="0 0 12 8" style={{ opacity: 0.5 }}>
                <path d="M0 4 Q3 0 6 4 Q9 0 12 4" stroke="rgba(200,220,200,0.7)" strokeWidth="1" fill="none" />
              </svg>
            </motion.div>
          ))}

          {/* Fireflies */}
          <FireflyParticles count={isEmpty ? 10 : 30} containerRef={containerRef} />

          {/* Fruit nodes */}
          {positioned.map(({ fruit, x, y }, i) => {
            const branch = branches.find(
              (b) => b.recipientName.toLowerCase() === fruit.recipientName.toLowerCase()
            );
            const health = branch ? getBranchHealth(branch.lastSentAt) : 'active';
            return (
              <FruitNode
                key={fruit.id}
                fruit={fruit}
                x={x}
                y={y}
                index={i}
                onClick={setSelectedFruit}
                isDimmed={health === 'dormant'}
              />
            );
          })}

          {/* Empty state */}
          {isEmpty && <EmptyTreeState />}

          {/* Ground glow — shifts warm at sunrise */}
          <div
            className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
            style={{
              background: stage.skyPhase >= 2
                ? 'linear-gradient(to top, hsla(35, 50%, 30%, 0.1), transparent)'
                : 'linear-gradient(to top, hsla(120, 40%, 40%, 0.06), transparent)',
            }}
          />
        </div>

        {/* Relationship panel */}
        {!isEmpty && (
          <div className="lg:w-[300px] flex-shrink-0">
            <RelationshipPanel
              branches={branches}
              totalFruits={fruits.length}
            />
          </div>
        )}
      </div>

      {/* Branch nudge */}
      {!isEmpty && <BranchNudge branches={branches} />}

      {/* Fruit detail modal */}
      <FruitModal
        fruit={selectedFruit}
        onClose={() => setSelectedFruit(null)}
        onShare={handleShare}
      />

      {/* Full Share Dialog */}
      {shareDialogFruit && shareDialogFruit.publicSlug && (
        <ShareDialog
          open={!!shareDialogFruit}
          onOpenChange={(open) => { if (!open) setShareDialogFruit(null); }}
          publicSlug={shareDialogFruit.publicSlug}
          recipientName={shareDialogFruit.recipientName}
          announcementId={shareDialogFruit.id}
          template={shareDialogFruit.template}
          relationship={shareDialogFruit.relationship}
        />
      )}
    </div>
  );
}
